import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Settings2, Save, CheckCircle2, BellOff } from 'lucide-react';
import { walletIntelApi } from '@/api/walletIntel';
import { WalletIntelSubNav } from '@/components/wallet-intel/SubNav';

const CHAINS = [
  { value: 'bitcoin', label: 'Bitcoin' },
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'tron', label: 'Tron' },
];

export default function WalletIntelSettings() {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['wallet-intel-settings'],
    queryFn: () => walletIntelApi.getSettings(),
  });

  const [defaultChain, setDefaultChain] = useState('ethereum');
  const [defaultMaxHops, setDefaultMaxHops] = useState(1);
  const [defaultMaxFanout, setDefaultMaxFanout] = useState(10);
  const [includeExposureByDefault, setIncludeExposureByDefault] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settingsQuery.data) {
      setDefaultChain(settingsQuery.data.default_chain);
      setDefaultMaxHops(settingsQuery.data.default_max_hops);
      setDefaultMaxFanout(settingsQuery.data.default_max_fanout);
      setIncludeExposureByDefault(settingsQuery.data.include_exposure_by_default);
    }
  }, [settingsQuery.data]);

  const mutation = useMutation({
    mutationFn: () =>
      walletIntelApi.updateSettings({
        default_chain: defaultChain,
        default_max_hops: defaultMaxHops,
        default_max_fanout: defaultMaxFanout,
        include_exposure_by_default: includeExposureByDefault,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['wallet-intel-settings'], data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  const inputStyle: React.CSSProperties = {
    background: '#050709', border: '1px solid #182030', color: '#DCE8F5', fontSize: 12.5,
  };

  return (
    <div style={{ background: '#050709', minHeight: '100%' }} className="flex flex-col -m-6 p-6">
      <WalletIntelSubNav />

      <div style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12 }} className="px-6 py-4 mb-6">
        <h1 style={{ color: '#F2F6FC', fontFamily: "'Space Grotesk', monospace", fontWeight: 800, fontSize: 20 }}>Settings</h1>
        <p style={{ color: '#8D96AC', fontSize: 13, marginTop: 3 }}>
          Your personal defaults for Wallet Lookup and Batch Screening — saved to your analyst profile.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, padding: 22 }}
          className="flex flex-col gap-5"
        >
          <div className="flex items-center gap-2.5">
            <div style={{ width: 32, height: 32, borderRadius: 9, background: '#00C8FF18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Settings2 size={16} style={{ color: '#00C8FF' }} />
            </div>
            <span style={{ color: '#F2F6FC', fontWeight: 700, fontSize: 15 }}>Screening Defaults</span>
          </div>

          <div>
            <p className="tracking-[0.1em] mb-2" style={{ color: '#7C8CA6', fontSize: 11.5, fontWeight: 700 }}>DEFAULT CHAIN</p>
            <select value={defaultChain} onChange={(e) => setDefaultChain(e.target.value)} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle}>
              {CHAINS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <p style={{ color: '#5A7A9A', fontSize: 11, marginTop: 6 }}>Pre-selected on Wallet Lookup and Batch Screening when no chain is specified.</p>
          </div>

          <div>
            <p className="tracking-[0.1em] mb-2" style={{ color: '#7C8CA6', fontSize: 11.5, fontWeight: 700 }}>DEFAULT EXPOSURE SEARCH DEPTH</p>
            <div className="flex items-center gap-1.5">
              {[1, 2].map((n) => {
                const active = defaultMaxHops === n;
                return (
                  <motion.button
                    key={n}
                    whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                    onClick={() => setDefaultMaxHops(n)}
                    className="px-4 py-2 rounded-lg font-bold transition-all"
                    style={{
                      background: active ? '#00C8FF18' : '#050709',
                      color: active ? '#00C8FF' : '#8D96AC',
                      border: `1px solid ${active ? '#00C8FF44' : '#182030'}`,
                      fontSize: 12,
                    }}
                  >
                    {n} HOP{n > 1 ? 'S' : ''}
                  </motion.button>
                );
              })}
            </div>
            <p style={{ color: '#5A7A9A', fontSize: 11, marginTop: 6 }}>How far the Exposure tab traces by default before you adjust it per-address.</p>
          </div>

          <div>
            <p className="tracking-[0.1em] mb-2" style={{ color: '#7C8CA6', fontSize: 11.5, fontWeight: 700 }}>DEFAULT FANOUT LIMIT PER HOP</p>
            <input
              type="number" min={1} max={25} value={defaultMaxFanout}
              onChange={(e) => setDefaultMaxFanout(Math.min(25, Math.max(1, Number(e.target.value) || 1)))}
              className="w-28 rounded-lg px-3 py-2 outline-none font-mono" style={inputStyle}
            />
            <p style={{ color: '#5A7A9A', fontSize: 11, marginTop: 6 }}>
              Max counterparties explored per hop in Relationships/Exposure — higher finds more but costs more live checks per address (1–25).
            </p>
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={includeExposureByDefault} onChange={(e) => setIncludeExposureByDefault(e.target.checked)} />
            <span style={{ color: '#8D96AC', fontSize: 12.5 }}>Include exposure checks by default in Batch Screening (slower, more live checks per address)</span>
          </label>

          <div className="flex items-center gap-3 mt-1">
            <motion.button
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || settingsQuery.isLoading}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-bold tracking-wide transition-all disabled:opacity-40"
              style={{ background: '#00C8FF18', color: '#00C8FF', border: '1px solid #00C8FF44', fontSize: 13 }}
            >
              <Save size={14} /> SAVE DEFAULTS
            </motion.button>
            {saved && (
              <motion.span
                initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-1.5" style={{ color: '#00C853', fontSize: 12.5, fontWeight: 600 }}
              >
                <CheckCircle2 size={15} /> Saved
              </motion.span>
            )}
            {mutation.isError && (
              <span style={{ color: '#FF1F1F', fontSize: 12 }}>{(mutation.error as Error)?.message}</span>
            )}
          </div>
        </motion.div>

        {/* Honest placeholder — no notification delivery exists in this module yet, so this
            deliberately doesn't offer a toggle that would silently do nothing. */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.05 }}
          style={{ background: '#0C1118', border: '1px dashed #182030', borderRadius: 12, padding: 22 }}
          className="flex flex-col items-center text-center justify-center gap-3"
        >
          <div style={{ width: 44, height: 44, borderRadius: 12, background: '#182030', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BellOff size={20} style={{ color: '#5A7A9A' }} />
          </div>
          <p style={{ color: '#DCE8F5', fontWeight: 700, fontSize: 14 }}>Notification preferences</p>
          <p style={{ color: '#8D96AC', fontSize: 12.5, lineHeight: 1.6, maxWidth: 320 }}>
            Not built yet — there's no alert-delivery channel (email/Slack/webhook) wired up for this module,
            so this isn't offered as a toggle until it can actually notify you of something.
          </p>
          <span style={{ background: '#F0B42918', color: '#F0B429', border: '1px solid #F0B42930', fontSize: 10.5 }}
            className="font-bold px-2.5 py-1 rounded-full tracking-wider">
            COMING SOON
          </span>
        </motion.div>
      </div>
    </div>
  );
}
